import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { purchaseColumns, purchaseRow } from "./tableParams/purchaseColumns";
import StatsAPI from "../../../api/StatsAPI";
import { navigatorLanguage } from "../../../i18n";
import { getLanguageUuid } from "../../../utils/data/Content";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ExportCsvButton from "../charts/ExportCsvButton";

import type { JSX } from "react";

import type { AnalyticsPeriod } from "@/src/hooks/usePeriodFilter";

import type { PurchaseRow } from "./tableParams/purchaseColumns.types";
import type { AppState } from "../../../reducers";

interface PurchasesTableProps {
  period: AnalyticsPeriod;
  userAbility: boolean;
  offerAbility: boolean;
}

function PurchasesTable({
  period,
  userAbility,
  offerAbility,
}: PurchasesTableProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { startDate, endDate } = period;

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const { language_uuid: languageUuid } = getLanguageUuid(
    languages,
    navigatorLanguage,
  );

  const getPurchasesTransactionsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getPurchasesTransactions,
    params: [{ projectSlug, startDate, endDate, languageUuid }],
  });
  const {
    data: purchasesTransactions,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getPurchasesTransactionsQuery,
    enabled: !!projectSlug,
  });

  const rowList = useMemo<PurchaseRow[]>(
    () => (purchasesTransactions?.data ?? []).map(purchaseRow),
    [purchasesTransactions?.data],
  );

  const columnList = useMemo(
    () => purchaseColumns({ t, projectSlug, userAbility, offerAbility }),
    [t, projectSlug, userAbility, offerAbility],
  );

  // Toute la période, côté serveur : sans la recherche ni les filtres.
  const fetchExport = async (): Promise<string> =>
    StatsAPI.getPurchasesTransactions({
      projectSlug,
      startDate,
      endDate,
      languageUuid,
      isExport: true,
    });

  const canExport = !isLoading && rowList.length > 0;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("purchaseTransactionsTitle")}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <ExportCsvButton
            disabled={!canExport}
            fetchCsv={fetchExport}
            fileName="purchases_transactions.csv"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "purchase_date", desc: true }}
          emptyText={t("noDatas")}
          getRowId={(row) => row.purchase_uuid}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          resetPageOn={[startDate.valueOf(), endDate.valueOf()]}
          storageKey="transactions-purchases"
          urlSync={{ prefix: "purchases" }}
        />
      </CardContent>
    </Card>
  );
}

export default PurchasesTable;
